import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Send, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner";

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Notifications() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminForm, setAdminForm] = useState({ title: "", body: "", link_url: "", target: "students", send_email: false });
  const [sending, setSending] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);

  const loadNotifications = async () => {
    const { data } = await api.get("/notifications");
    setItems(data.items || []);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await loadNotifications();
        if (user?.role === "admin") {
          try {
            const { data } = await api.get("/admin/email/smtp-status");
            setSmtpStatus(data);
          } catch {}
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Impossible de charger les notifications.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user?.role]);

  const markRead = async (notification) => {
    if (notification.read_at) return;
    try {
      await api.patch(`/notifications/${notification.notification_id}/read`);
      setItems((prev) => prev.map((n) => n.notification_id === notification.notification_id ? { ...n, read_at: new Date().toISOString() } : n));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de marquer les notifications comme lues.");
    }
  };

  const sendAdminNotification = async (e) => {
    e.preventDefault();
    if (!adminForm.title.trim()) return;
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/notifications/admin", adminForm);
      setAdminForm({ title: "", body: "", link_url: "", target: "students", send_email: false });
      const emailInfo = adminForm.send_email
        ? `\nEmails envoyés : ${data.emails_sent || 0}${data.emails_failed ? ` — échecs : ${data.emails_failed}` : ""}`
        : "";
      alert(`Notification envoyée.${emailInfo}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’envoyer la notification.");
    } finally {
      setSending(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    setError("");
    try {
      await api.post("/admin/email/test", { email: user?.email });
      alert(`Email de test envoyé à ${user?.email}.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’envoyer l’email de test.");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="notifications-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="label">Centre de notifications</p>
          <h1 className="font-heading text-4xl font-black">Notifications</h1>
          <p className="text-gray-600 mt-2">Retrouve ici tes messages importants, annonces et nouveautés SF Learning.</p>
        </div>
        {items.some((n) => !n.read_at) && (
          <button type="button" onClick={markAllRead} className="btn-ghost self-start md:self-auto">
            <CheckCheck className="h-4 w-4" /> Tout marquer comme lu
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {user?.role === "admin" && (
        <form onSubmit={sendAdminNotification} className="card p-5 mb-8 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-green" />
              <h2 className="font-heading text-xl font-bold">Envoyer une notification admin</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`rounded-full px-3 py-1 font-semibold ${smtpStatus?.configured ? "bg-emerald-50 text-brand-green" : "bg-amber-50 text-amber-700"}`}>
                SMTP {smtpStatus?.configured ? "configuré" : "non configuré"}
              </span>
              <button
                type="button"
                onClick={sendTestEmail}
                disabled={testingEmail || !smtpStatus?.configured}
                className="rounded-full border border-emerald-200 px-3 py-1 font-semibold text-brand-green transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {testingEmail ? "Test..." : "Envoyer un test"}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label mb-1 block">Titre</label>
              <input
                className="input"
                value={adminForm.title}
                onChange={(e) => setAdminForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Ex : Nouveau module disponible"
                maxLength={180}
              />
            </div>
            <div>
              <label className="label mb-1 block">Lien optionnel</label>
              <input
                className="input"
                value={adminForm.link_url}
                onChange={(e) => setAdminForm((f) => ({ ...f, link_url: e.target.value }))}
                placeholder="/dashboard ou /catalog"
              />
            </div>
          </div>
          <div>
            <label className="label mb-1 block">Message</label>
            <textarea
              className="input min-h-[90px]"
              value={adminForm.body}
              onChange={(e) => setAdminForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Ton message aux apprenants..."
              maxLength={1000}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <select
              className="input max-w-xs"
              value={adminForm.target}
              onChange={(e) => setAdminForm((f) => ({ ...f, target: e.target.value }))}
            >
              <option value="students">Apprenants uniquement</option>
              <option value="all">Tout le monde</option>
            </select>
            <label className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 px-3 py-2 text-sm font-semibold text-gray-700">
              <input
                type="checkbox"
                checked={adminForm.send_email}
                onChange={(e) => setAdminForm((f) => ({ ...f, send_email: e.target.checked }))}
                disabled={!smtpStatus?.configured}
              />
              Envoyer aussi par email
            </label>
            <button type="submit" disabled={sending || !adminForm.title.trim()} className="btn-primary">
              <Send className="h-4 w-4" /> Envoyer
            </button>
          </div>
          {!smtpStatus?.configured && (
            <p className="text-xs text-amber-700">
              Pour activer l’envoi email, ajoute SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD et SMTP_FROM dans les Secrets Emergent.
            </p>
          )}
        </form>
      )}

      <div className="card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Bell className="h-12 w-12 text-emerald-200 mx-auto mb-3" />
            <p className="font-semibold text-gray-700">Aucune notification pour le moment.</p>
          </div>
        ) : (
          <div className="divide-y divide-emerald-100">
            {items.map((n) => {
              const content = (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => markRead(n)}
                  onKeyDown={(e) => e.key === "Enter" && markRead(n)}
                  className={`w-full text-left p-5 transition ${n.read_at ? "bg-white" : "bg-emerald-50/70"}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 h-3 w-3 rounded-full ${n.read_at ? "bg-gray-200" : "bg-brand-orange"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-heading font-bold text-lg">{n.title}</p>
                        {!n.read_at && <span className="rounded-full bg-brand-orange px-2 py-0.5 text-[11px] font-bold text-white">Nouveau</span>}
                      </div>
                      {n.body && <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{n.body}</p>}
                      <p className="text-xs text-gray-400 mt-2">{formatDate(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              );

              return n.link_url ? (
                <Link key={n.notification_id} to={n.link_url} onClick={() => markRead(n)} className="block hover:bg-emerald-50">
                  {content}
                </Link>
              ) : (
                <div key={n.notification_id}>{content}</div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
